import { ApolloClient, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { useMemo } from 'react';
import { omitTypenameLink } from './links/omitTypenameLink';
import { splitLink } from './links/httpLink';
import { cache } from './cache/ApolloCache';
import { toastResolvers } from '@app/toasts/resolvers/toastResolvers';
import { useAuth0 } from '@auth0/auth0-react';

export const useCreateApolloClient = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const client = useMemo(() => {
    const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path, extensions }) => {
          console.error(`GraphQL error: ${message}`, {
            locations,
            path,
            extensions,
            operation: operation.operationName,
          });
        });
      }

      if (networkError) {
        console.error(`Network error: ${networkError.message}`, {
          operation: operation.operationName,
          error: networkError,
        });
      }
    });

    const retryLink = new RetryLink({
      delay: {
        initial: 300,
        max: Infinity,
        jitter: true,
      },
      attempts: {
        max: 3,
        retryIf: (error) => {
          return !!error && (error.networkError?.statusCode >= 500 || error.networkError?.statusCode === 429);
        },
      },
    });

    const authLink = setContext(async (operation, { headers = {} }) => {
      if (!isAuthenticated) {
        return { headers };
      }
      if (operation.context?.unauthorized) {
        return { headers };
      }

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: import.meta.env.PUBLIC_AUTH0_AUDIENCE,
            scope: 'openid profile email offline_access',
          },
        });
        return {
          headers: {
            ...headers,
            Authorization: token ? `Bearer ${token}` : '',
          },
        };
      } catch (error) {
        console.error('Failed to get authentication token:', error);
        return { headers };
      }
    });

    const transport = splitLink(async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: import.meta.env.PUBLIC_AUTH0_AUDIENCE,
            scope: 'openid profile email offline_access',
          },
        });
        return token ?? '';
      } catch (error) {
        console.error('Failed to get WebSocket token:', error);
        return '';
      }
    });

    const link = from([errorLink, retryLink, omitTypenameLink, authLink, transport]);

    return new ApolloClient({
      link,
      cache,
      devtools: {
        enabled: process.env.NODE_ENV === 'development',
        name: 'Polariks Platform',
      },
      resolvers: {
        Mutation: {
          ...toastResolvers.Mutation,
        },
      },
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-and-network',
          nextFetchPolicy: 'cache-first',
          errorPolicy: 'all',
          notifyOnNetworkStatusChange: true,
        },
        query: {
          fetchPolicy: 'cache-first',
          errorPolicy: 'all',
        },
        mutate: {
          errorPolicy: 'all',
        },
      },
      assumeImmutableResults: true,
      queryDeduplication: true,
      connectToDevTools: process.env.NODE_ENV === 'development',
      ssrMode: typeof window === 'undefined',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return client;
};

export default useCreateApolloClient;
